import ko from "knockout";
import * as _ from "underscore";
import manifestManager, { IRole } from "flow/utils/manifestManager";
import config from "flow/config";
import dataService from "flow/data/service";
import MyFlow from "flow/MyFlow";
import { CloseCallback, Dictionary } from "flow/api/types";

interface IModule {
    name: string;
    id: string;
    enabled: KnockoutObservable<boolean>;
    blacklisted: boolean;
    roles: ISubRole[];
    isEmpty: boolean;
    isBroken: boolean;
    inactiveReason: string;
    statusText: string;
}

interface ISubRole {
    name: string;
    id: string;
    enabled: KnockoutObservable<boolean>;
}

interface IRoleChangeEvent {
    selectedRole: ISubRole;
    previousRole: ISubRole;
}

export default function ModuleSelectorViewModel(myFlow: MyFlow) {
    const modules = ko.observableArray<IModule>();
    const useCustomView = ko.observable(false);

    let closeCallback: CloseCallback;

    function activate(_viewContext: Dictionary, closeCb: CloseCallback) {
        closeCallback = closeCb;
        refreshTree();
    }

    function refreshTree() {
        const modulesList = [];

        const roleTree = manifestManager.getRoleTree();
        const blacklist = manifestManager.getBlacklist();
        const onDemandSharedModuleList = manifestManager.getOnDemandSharedModuleIds();
        for (const moduleId in roleTree) {
            if (moduleId === config.moduleId || moduleId === config.systemInfoModuleId) {
                continue;
            }

            if (onDemandSharedModuleList.indexOf(moduleId.toLowerCase()) > -1) {
                continue;
            }

            const moduleRole = roleTree[moduleId];
            if (moduleRole.isRoleSelectionExclusive == true) {
                useCustomView(true);
            }

            const enabled = isEnabled(blacklist, moduleId);

            const entry: IModule = {
                name: moduleRole.name,
                id: moduleId,
                enabled: ko.observable(enabled),
                blacklisted: !enabled,
                roles: getSubRoles(moduleRole, blacklist),
                isEmpty: roleTree[moduleId].isEmpty,
                isBroken: roleTree[moduleId].isBroken,
                statusText: getStatusText(roleTree[moduleId], enabled),
                inactiveReason: roleTree[moduleId].inactiveReason
            };

            if (moduleRole.isRoleSelectionExclusive == true) {
                useCustomView(true);
            }

            modulesList.push(entry);
        }

        sortModulesAlphabetically(modulesList);

        modules(modulesList);
    }

    function isEnabled(blacklist: string[], id: string) {
        return !_.contains(blacklist, id);
    }

    function getStatusText(roleObj: IRole, enabled: boolean) {
        if (roleObj.isBroken) {
            return ` (${smallerCaseMeta("FlowHtml.Translations.CallFailedToReadModule")})`;
        }

        if (roleObj.inactiveReason) {
            return ` (${roleObj.inactiveReason})`;
        }

        if (enabled && roleObj.isEmpty) {
            return ` (${smallerCaseMeta("FlowHtml.Translations.NoPermissions")})`;
        }

        return undefined;
    }

    function smallerCaseMeta(key: string) {
        return myFlow.translate(key).toLocaleLowerCase(myFlow.getUiCulture());
    }

    function sortModulesAlphabetically(modulesList: IModule[]) {
        modulesList.sort((a, b) => (a.name > b.name ? 1 : -1));
    }

    function getSubRoles(moduleRole: IRole, blacklist: string[]): ISubRole[] {
        if (useCustomView() == false) {
            return _.map(moduleRole.roles, function (role) {
                return {
                    name: role.name,
                    id: role.id,
                    enabled: ko.observable(isEnabled(blacklist, role.id))
                };
            });
        } else {
            return _.map(moduleRole.roles, function (role) {
                return {
                    name: role.name,
                    id: role.id,
                    enabled: ko.observable(role.isEnabled)
                };
            });
        }
    }

    function onApply() {
        const newBlacklist = generateBlackList();
        dataService
            .saveBlacklist(myFlow, newBlacklist)
            .done(function () {
                myFlow.refreshFlow();
            })
            .fail(function (jqXhr) {
                myFlow.showAjaxErrorDialog(jqXhr, myFlow.translate("FlowHtml.Translations.SaveFailed"));
            });
    }

    function generateBlackList() {
        const oldBlacklist = manifestManager.getBlacklist();

        // We will start from the old list so that blacklisted sub roles for nonloaded web modules won't get lost.
        const newBlacklist = oldBlacklist.slice(); // Clone old

        _.each(modules(), function (module) {
            addRemoveRole(newBlacklist, module.id, module.enabled());

            _.each(module.roles, function (role) {
                addRemoveRole(newBlacklist, role.id, role.enabled());
            });
        });

        return newBlacklist;
    }

    function addRemoveRole(list: string[], roleId: string, enabled: boolean) {
        if (enabled === false) {
            if (!_.contains(list, roleId)) {
                list.push(roleId);
            }
        } else {
            if (_.contains(list, roleId)) {
                list.splice(_.indexOf(list, roleId), 1);
            }
        }
    }

    function onCancel() {
        refreshTree();
        closeCallback();
    }

    function onRoleSelect(selectedRole: ISubRole) {
        let previousRole: ISubRole = undefined;
        _.each(modules(), function (module) {
            _.each(module.roles, function (role) {
                if (role.enabled() == true) {
                    previousRole = role;
                }
                if (role.id == selectedRole.id) {
                    role.enabled(true);
                }
            });
        });
        if (previousRole) {
            previousRole.enabled(false);
        }
        const roles: IRoleChangeEvent = {
            selectedRole: selectedRole,
            previousRole: previousRole
        };

        myFlow.publish(config.topics.roleChanged, roles);
    }

    function hasNoContent(module: IModule) {
        return module.isEmpty && !module.blacklisted && !module.isBroken;
    }

    return {
        activate: activate,
        onApply: onApply,
        useCustomView: useCustomView,
        onCancel: onCancel,
        modules: modules,
        onRoleSelect: onRoleSelect,
        hasNoContent: hasNoContent
    };
}
